import { Controller, useForm } from 'react-hook-form';
import React, { useState } from 'react';
import { Button, InputText, Select, TextArea } from '../ui';
import countries, { countryToFlag } from '../../helpers/countries';
import { InputValidationError } from '../types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import SaveFooterLayout from '../../layouts/SaveFooterLayout';
import { AffiliationInputText } from '../AffiliationInputText/AffiliationInputText';
import { AddButton } from '../ui/inputs/AddButton/AddButton';
import range from '../../helpers/range';
import { getFullName } from '../../helpers/users';
import SimpleDialog from '../dialogs/SimpleDialog/SimpleDialog';
import { changeNameRequest } from '../../store/user/types';

interface FormFields {
  first_name: string;
  middle_name: string;
  last_name: string;
  country: string;
  organization: string;
  organization2: string;
  organization3: string;
  organization4: string;
  organization5: string;
}

interface Props {
  formData: FormFields;
  onSave: (data: FormFields) => void;
  onCancel?: () => void;
  validation_error?: { [k: string]: string };
  onChangeName?: (data: changeNameRequest) => void;
}

const PersonalInfoForm: React.FC<Props> = ({
  formData,
  onSave,
  onCancel,
  validation_error,
  onChangeName,
  children,
}) => {
  const { handleSubmit, errors, control, getValues } = useForm<FormFields>();
  const { t } = useTranslation();
  const currentOrganizations = [
    formData.organization,
    formData.organization2,
    formData.organization3,
    formData.organization4,
    formData.organization5,
  ];
  const [numExtra, setNumExtra] = useState(Math.max(0, currentOrganizations.map((value) => !!value).lastIndexOf(true)));
  const [dialog, setDialog] = useState<JSX.Element | undefined>(undefined);

  const frontend_validation_rules: {
    country: { required: string };
    organization: { required: string };
    organization2: { required: boolean };
    organization3: { required: boolean };
    organization4: { required: boolean };
    organization5: { required: boolean };
    last_name: { required: string };
    middle_name: { required: boolean };
    first_name: { required: string };
  } = {
    first_name: { required: t('This field is required') },
    middle_name: { required: false },
    last_name: { required: t('This field is required') },
    country: { required: t('This field is required') },
    organization: { required: t('This field is required') },
    organization2: { required: false },
    organization3: { required: false },
    organization4: { required: false },
    organization5: { required: false },
  };

  const validationErrorsByField: { [key in keyof FormFields]: InputValidationError } = {
    first_name: {},
    middle_name: {},
    last_name: {},
    country: {},
    organization: {},
    organization2: {},
    organization3: {},
    organization4: {},
    organization5: {},
  };

  // Frontend Validation
  if (Object.keys(errors).length > 0) {
    Object.keys(frontend_validation_rules).forEach((key) => {
      const field = key as keyof FormFields;
      const error = errors[field];
      if (error) {
        validationErrorsByField[field] = {
          ...validationErrorsByField[field],
          error: true,
          helperText: error.message,
        };
      }
    });
  } else {
    // Backend Validation
    if (validation_error) {
      Object.keys(frontend_validation_rules).forEach((key) => {
        const field = key as keyof FormFields;
        const error = validation_error[field];
        if (error) {
          validationErrorsByField[field] = {
            ...validationErrorsByField[field],
            error: true,
            helperText: error,
          };
        }
      });
    }
  }

  let countryOptions = countries
    .sort(function (a, b) {
      const nameA = a.label.toUpperCase(); // ignore upper and lowercase
      const nameB = b.label.toUpperCase(); // ignore upper and lowercase
      if (nameA < nameB) {
        return -1;
      }
      if (nameA > nameB) {
        return 1;
      }

      // names must be equal
      return 0;
    })
    .map((country) => {
      const label = countryToFlag(country.code) + ' ' + country.label;
      return { id: country.code, label };
    });

  countryOptions = [{ id: '', label: 'None' }].concat(countryOptions);

  const isNameValidated = !!onChangeName;

  const nameInputs = (
    <>
      <Controller
        name="first_name"
        control={control}
        defaultValue={formData.first_name}
        rules={frontend_validation_rules.first_name}
        render={({ onChange, value }) => (
          <InputText
            label={t('First name')}
            required={!!frontend_validation_rules.first_name.required}
            value={value}
            onChange={onChange}
            error={validationErrorsByField.first_name.error}
            helperText={validationErrorsByField.first_name.helperText}
            className="w-full"
          />
        )}
      />
      <Controller
        name="middle_name"
        control={control}
        defaultValue={formData.middle_name}
        rules={frontend_validation_rules.middle_name}
        render={({ onChange, value }) => (
          <InputText
            label={t('Middle name')}
            required={!!frontend_validation_rules.middle_name.required}
            value={value}
            onChange={onChange}
            error={validationErrorsByField.middle_name.error}
            helperText={validationErrorsByField.middle_name.helperText}
            className="w-full"
          />
        )}
      />
      <Controller
        name="last_name"
        control={control}
        defaultValue={formData.last_name}
        rules={frontend_validation_rules.last_name}
        render={({ onChange, value }) => (
          <InputText
            label={t('Last name')}
            required={!!frontend_validation_rules.last_name.required}
            value={value}
            onChange={onChange}
            error={validationErrorsByField.last_name.error}
            helperText={validationErrorsByField.last_name.helperText}
            className="w-full"
          />
        )}
      />
      {isNameValidated && (
        <Controller
          name="reason"
          control={control}
          defaultValue=""
          render={({ onChange, value }) => (
            <TextArea
              label={t('Reason')}
              required={true}
              value={value}
              onChange={onChange}
              className="w-full"
              rows={5}
            />
          )}
        />
      )}
    </>
  );

  const form = (
    <form onSubmit={handleSubmit(onSave)} className="flex flex-col mobileCtrlProfileForm" style={{ flexGrow: 1 }}>
      <StyledFormContent style={{ flexGrow: 1 }}>
        <div className="flex flex-col mx-10">
          {isNameValidated && (
            <div className="mt-3">
              <div>
                <span className="font-medium">{t('Full name')} </span>
              </div>
              <div>
                <span>
                  {getFullName({
                    first_name: formData.first_name,
                    middle_name: formData.middle_name,
                    last_name: formData.last_name,
                  })}
                </span>
                <Button
                  type="button"
                  variant="text"
                  onClick={() =>
                    setDialog(
                      <SimpleDialog
                        handleOk={() => {
                          onChangeName({
                            first_name: getValues('first_name'),
                            middle_name: getValues('middle_name'),
                            last_name: getValues('last_name'),
                            reason: getValues('reason'),
                          });
                        }}
                        handleClose={() => setDialog(undefined)}
                        width="32rem"
                        okButtonText={t('Submit request')}
                      >
                        <p>
                          {t(
                            'To change the name on your user account submit a request to the system administrators. This request will be reviewed and processed within 48 hours.',
                          )}
                        </p>
                        <div className="flex flex-col gap-y-3 mt-5">{nameInputs}</div>
                      </SimpleDialog>,
                    )
                  }
                  style={{ paddingLeft: '2rem', paddingRight: '0rem' }}
                >
                  {t('Request name change')}
                </Button>
              </div>
            </div>
          )}
          <div style={{ width: '24rem' }} className="mobileCtrlProfileFormContent">
            {!isNameValidated && nameInputs}
            <Controller
              name="country"
              control={control}
              defaultValue={formData.country}
              rules={frontend_validation_rules.country}
              render={({ onChange, value }) => (
                <Select
                  label={t('Country')}
                  required={!!frontend_validation_rules.country.required}
                  value={value}
                  options={countryOptions}
                  onChange={onChange}
                  error={validationErrorsByField.country.error}
                  helperText={validationErrorsByField.country.helperText}
                  width="100%"
                />
              )}
            />
            <Controller
              name="organization"
              control={control}
              defaultValue={formData.organization}
              rules={frontend_validation_rules.organization}
              render={({ onChange, value }) => (
                <AffiliationInputText
                  label={t('Organization')}
                  required={!!frontend_validation_rules.organization.required}
                  value={value}
                  onChange={onChange}
                  error={validationErrorsByField.organization.error}
                  helperText={validationErrorsByField.organization.helperText}
                />
              )}
            />

            {range(1, numExtra).map((value) => {
              const key = `organization${value + 1}` as
                | 'organization2'
                | 'organization3'
                | 'organization4'
                | 'organization5';
              const label = `${t('Organization')} ${value + 1}`;
              return (
                <Controller
                  key={key}
                  name={key}
                  control={control}
                  defaultValue={formData[key]}
                  rules={frontend_validation_rules[key]}
                  render={({ onChange, value }) => (
                    <AffiliationInputText
                      label={label}
                      required={!!frontend_validation_rules[key].required}
                      value={value}
                      onChange={onChange}
                      error={validationErrorsByField[key].error}
                      helperText={validationErrorsByField[key].helperText}
                    />
                  )}
                />
              );
            })}
            {numExtra < currentOrganizations.length - 1 && (
              <div className="mt-1">
                <AddButton
                  label={t('Add more')}
                  onClick={(e) => {
                    e.preventDefault();
                    setNumExtra(numExtra + 1);
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </StyledFormContent>
      {children}
      {dialog}
    </form>
  );

  return children ? (
    form
  ) : (
    <SaveFooterLayout onSave={handleSubmit(onSave)} onCancel={onCancel}>
      {form}
    </SaveFooterLayout>
  );
};

const StyledFormContent = styled.div`
  display: flex;
  margin: 0 auto 4% auto;
  max-width: 930px;

  .k-widget.k-dropdown {
    width: 24rem;
  }

  label,
  .labelSelect {
    margin-top: 15px;
    margin-bottom: 5px;
  }
  .helperText {
    height: auto;
  }
`;

export default PersonalInfoForm;
